/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback routing
 *
 * @author Yakov Gushchin
 * @version 1.13.1
 * @since 1.12.0
 */

import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router';
import { createStructuredSelector } from 'reselect';

import withAsyncStateManagement from '../../modules/asyncStateManagment/WithAsyncStateManagement';
import { EBreadCrumbType } from '../../modules/breadCrumbs/breadCrumbsTypes';
import { AccessComponentKeyEnum } from '../../modules/role/constants/guard/guardConstants';
import { makeSelectLocalStorageSelectedAccount } from '../../modules/storage/storageSelectors';
import { IRootReducer } from '../../reducers/rootReducer';
import { IAppRouterConfiguration } from '../../routes/appRouterTypes';
import { CLIENT_ID, IRouteParams, RoutePath } from '../../routes/constants/routesConstants';
import PrivateRoute from '../../routes/PrivateRoute';
import { PageNotFound } from '../ErrorPage/PageNotFound';
import { IWidgetSearchAccountsItem } from '../PageDashboard/components/Widgets/WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import CashbackControl from './components/CashbackTabControl/CashbackControl';
import CashbackHistory from './components/CashbackTabHistory/CashbackHistory';
import PageCashback from './components/PageCashback';
import cashbackReducer from './pageCashbackReducer';
import { cashbackSagas } from './sagas/cashbackSaga';

export const PageCashbackRouteConfiguration: IAppRouterConfiguration[] = [
    {
        path: RoutePath.cashbackAccountId,
        breadCrumbs: [EBreadCrumbType.accounts, EBreadCrumbType.accountId, EBreadCrumbType.accountCashback],
    },
    {
        path: RoutePath.cashback,
        breadCrumbs: [EBreadCrumbType.accountCashback],
    },
];

const PageCashbackRoute = ({ accountLocalStorage, match }: RouteComponentProps<IRouteParams> & IConnectedProps) => {
    return (
        <PageCashback id={match.params.clientId}>
            <Switch>
                <Route
                    exact
                    component={CashbackControl}
                    path={RoutePath.cashbackControlAccountId}
                />
                <Route
                    exact
                    component={CashbackHistory}
                    path={RoutePath.cashbackHistoryAccountId}
                />

                <Route exact path={RoutePath.cashback}>
                    <Redirect
                        to={RoutePath.cashbackControlAccountId.replace(CLIENT_ID, accountLocalStorage.id.toString())}
                    />
                </Route>

                <Route exact path={RoutePath.cashbackAccountId}>
                    <Redirect to={RoutePath.cashbackControlAccountId.replace(CLIENT_ID, match.params.clientId)}/>
                </Route>

                <PrivateRoute
                    accessKey={AccessComponentKeyEnum.PageNotFound}
                    component={PageNotFound}
                    path="**"
                />
            </Switch>
        </PageCashback>
    );
};

export interface IConnectedProps {
    accountLocalStorage: IWidgetSearchAccountsItem;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountLocalStorage: makeSelectLocalStorageSelectedAccount(),
});

export default withAsyncStateManagement(
    connect(mapStateToProps)(PageCashbackRoute),
    { sagas: cashbackSagas, reducer: { cashback: cashbackReducer } },
);
